// Context provider and modal UI for web alerts
import { useEffect, useState, useCallback, createContext, useContext } from 'react';
import {
  Modal,
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  TouchableWithoutFeedback,
  Platform,
  ScrollView,
} from 'react-native';
import { useCurrentTheme } from '@/theming/useCurrentTheme';
import { ThemeType } from '@/theming/baseTheme';
import Alert from './Alert';
import type { AlertButton, AlertOptions } from './AlertTypes';

interface AlertData {
  id: string;
  title: string;
  message?: string;
  buttons: AlertButton[];
  options?: AlertOptions;
}

interface AlertContextValue {
  showAlert: (
    title: string,
    message?: string,
    buttons?: AlertButton[],
    options?: AlertOptions
  ) => void;
}

const AlertContext = createContext<AlertContextValue | null>(null);

export const useAlertContext = () => {
  const context = useContext(AlertContext);
  if (!context) {
    throw new Error('useAlertContext must be used within AlertProvider');
  }
  return context;
};

// Web-only provider
const AlertProviderWeb: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [alertQueue, setAlertQueue] = useState<AlertData[]>([]);
  const [currentAlert, setCurrentAlert] = useState<AlertData | null>(null);
  const { theme } = useCurrentTheme();

  const showAlert = useCallback(
    (title: string, message?: string, buttons?: AlertButton[], options?: AlertOptions) => {
      const alertData: AlertData = {
        id: Date.now().toString() + Math.random(),
        title,
        message,
        buttons: buttons || [{ text: 'OK' }],
        options
      };

      setAlertQueue((prev) => [...prev, alertData]);
    },
    []
  );

  // Register show callback with Alert class (web only)
  useEffect(() => {
    // On web, Alert has setShowCallback method
    if (Platform.OS === 'web' && 'setShowCallback' in Alert) {
      (Alert as any).setShowCallback(showAlert);
      return () => {
        (Alert as any).setShowCallback(null);
      };
    }
  }, [showAlert]);

  // Process queue
  useEffect(() => {
    if (!currentAlert && alertQueue.length > 0) {
      setCurrentAlert(alertQueue[0]);
      setAlertQueue((prev) => prev.slice(1));
    }
  }, [currentAlert, alertQueue]);

  const closeAlert = useCallback(() => {
    if (currentAlert?.options?.onDismiss) {
      currentAlert.options.onDismiss();
    }
    setCurrentAlert(null);
  }, [currentAlert]);

  const handleButtonPress = useCallback(
    (button: AlertButton) => {
      closeAlert();
      if (button.onPress) {
        // Execute async callbacks
        Promise.resolve(button.onPress()).catch(console.error);
      }
    },
    [closeAlert]
  );

  const handleBackdropPress = useCallback(() => {
    if (currentAlert?.options?.cancelable !== false) {
      closeAlert();
    }
  }, [currentAlert, closeAlert]);

  // Keyboard handling
  useEffect(() => {
    if (!currentAlert) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && currentAlert.options?.cancelable !== false) {
        e.preventDefault();
        closeAlert();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        // Press first non-cancel button
        const defaultButton = currentAlert.buttons.find((b) => b.style !== 'cancel');
        if (defaultButton) {
          handleButtonPress(defaultButton);
        }
      }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [currentAlert, closeAlert, handleButtonPress]);

  const styles = createStyles(theme);

  return (
    <AlertContext.Provider value={{ showAlert }}>
      {children}

      <Modal
        visible={!!currentAlert}
        transparent
        animationType="fade"
        onRequestClose={handleBackdropPress}
      >
        <TouchableWithoutFeedback onPress={handleBackdropPress}>
          <View style={styles.backdrop}>
            <TouchableWithoutFeedback>
              <View style={styles.alertContainer}>
                <View style={styles.alertContent}>
                  {/* Title */}
                  <Text style={styles.title}>{currentAlert?.title}</Text>

                  {/* Message */}
                  {currentAlert?.message && (
                    <ScrollView
                      style={styles.messageContainer}
                      contentContainerStyle={styles.messageContent}
                    >
                      <Text style={styles.message}>{currentAlert.message}</Text>
                    </ScrollView>
                  )}

                  {/* Buttons */}
                  <View
                    style={
                      (currentAlert?.buttons.length || 0) > 2
                        ? styles.buttonsVertical
                        : styles.buttonsHorizontal
                    }
                  >
                    {currentAlert?.buttons.map((button, index) => {
                      const isCancel = button.style === 'cancel';
                      const isDestructive = button.style === 'destructive';

                      return (
                        <TouchableOpacity
                          key={index}
                          style={[
                            styles.button,
                            isCancel && styles.buttonCancel,
                            isDestructive && styles.buttonDestructive,
                          ]}
                          onPress={() => handleButtonPress(button)}
                          accessibilityRole="button"
                          accessibilityLabel={button.text}
                        >
                          <Text
                            style={[
                              styles.buttonText,
                              isCancel && styles.buttonTextCancel,
                              isDestructive && styles.buttonTextDestructive,
                            ]}
                          >
                            {button.text || 'OK'}
                          </Text>
                        </TouchableOpacity>
                      );
                    })}
                  </View>
                </View>
              </View>
            </TouchableWithoutFeedback>
          </View>
        </TouchableWithoutFeedback>
      </Modal>
    </AlertContext.Provider>
  );
};

// Native platforms: no-op provider
const AlertProviderNative: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return <>{children}</>;
};

// Export appropriate provider based on platform
const AlertProvider = Platform.OS === 'web' ? AlertProviderWeb : AlertProviderNative;

export default AlertProvider;

const createStyles = (theme: ThemeType) =>
  StyleSheet.create({
    backdrop: {
      flex: 1,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      justifyContent: 'center',
      alignItems: 'center',
      padding: theme.spacing.lg,
    },
    alertContainer: {
      width: '100%',
      maxWidth: 400,
    },
    alertContent: {
      backgroundColor: theme.colors.surface,
      borderRadius: theme.borderRadius.lg,
      padding: theme.spacing.lg,
      ...Platform.select({
        web: {
          // @ts-ignore - web-only style
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
        },
      }),
    },
    title: {
      fontSize: theme.fontSize.lg,
      fontWeight: 'bold',
      color: theme.colors.text,
      marginBottom: theme.spacing.sm,
      textAlign: 'center',
    },
    messageContainer: {
      maxHeight: 300,
      marginBottom: theme.spacing.lg,
    },
    messageContent: {
      flexGrow: 1,
    },
    message: {
      fontSize: theme.fontSize.md,
      color: theme.colors.textSecondary,
      textAlign: 'center',
      lineHeight: 22,
    },
    buttonsHorizontal: {
      flexDirection: 'row',
      gap: theme.spacing.sm,
      justifyContent: 'flex-end',
    },
    buttonsVertical: {
      flexDirection: 'column',
      gap: theme.spacing.sm,
    },
    button: {
      paddingVertical: theme.spacing.md,
      paddingHorizontal: theme.spacing.lg,
      borderRadius: theme.borderRadius.md,
      backgroundColor: theme.colors.primary,
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: 80,
    },
    buttonCancel: {
      backgroundColor: theme.colors.surface,
      borderWidth: 1,
      borderColor: theme.colors.border,
    },
    buttonDestructive: {
      backgroundColor: theme.colors.error,
    },
    buttonText: {
      fontSize: theme.fontSize.md,
      fontWeight: '600',
      color: '#ffffff',
    },
    buttonTextCancel: {
      color: theme.colors.text,
    },
    buttonTextDestructive: {
      color: '#ffffff',
    },
  });
